import type { OverviewSprint } from '@hygge/shared';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { paceVerdict } from '../../lib/sprint-pace';

/**
 * The first line of a board: whether the sprint is going to land.
 *
 * The Overview used to open with the board's own name and description — facts
 * the reader already had from the breadcrumb they clicked, and from the board
 * they chose. This opens with the one sentence worth interrupting someone for,
 * generated by `paceVerdict` from the same rule the Boards page quotes, so the
 * two screens cannot disagree about the same sprint.
 *
 * There is no forecast anywhere on this page and there cannot honestly be one:
 * no sprint in this product has ever been closed, so `sprints.velocity` is null
 * everywhere and a predicted ship date would be arithmetic over nothing. The
 * chip on the right says so in the reader's own numbers, and it is rendered
 * from the real count — once three sprints have been closed it stops appearing
 * on its own, with nobody having to remember to delete it.
 */

/** Below this many closed sprints, a velocity-based forecast would be invented. */
export const FORECAST_MIN_CLOSED_SPRINTS = 3;

interface SprintVerdictLineProps {
  /** The sprint the page is reporting on — the active one, or null when none is. */
  sprint: OverviewSprint | null;
  /** Sprints on this board with status `completed`. */
  closedSprintCount: number;
  className?: string;
}

export function SprintVerdictLine({
  sprint,
  closedSprintCount,
  className,
}: SprintVerdictLineProps) {
  const { subject, predicate, tone } = paceVerdict(sprint);

  return (
    <TooltipProvider delayDuration={150}>
      <section
        className={cn(
          'flex min-h-[72px] flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-border py-4',
          className,
        )}
        aria-label="Sprint pace"
      >
        <p className="max-w-[68ch] text-[19px] font-normal leading-[1.35] tracking-[-0.01em] text-muted-foreground">
          {subject && <span className="font-semibold text-foreground">{subject} </span>}
          <span className={cn(tone === 'risk' && 'text-foreground')}>{predicate}</span>
        </p>

        {closedSprintCount < FORECAST_MIN_CLOSED_SPRINTS && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0 cursor-help rounded-full border border-border px-2.5 py-1 text-[11px] tabular-nums text-muted-foreground">
                {closedSprintCount} closed sprint{closedSprintCount === 1 ? '' : 's'} · no forecast
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[44ch]">
              A ship-date forecast needs a velocity measured over at least{' '}
              {FORECAST_MIN_CLOSED_SPRINTS} closed sprints. This board has {closedSprintCount}, so
              nothing on this page is predicted — every figure here is a calendar fact or a count of
              work already done.
            </TooltipContent>
          </Tooltip>
        )}
      </section>
    </TooltipProvider>
  );
}
